import React from 'react';
import moment from 'moment';
import tinycolor from 'tinycolor2';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import { getLocationColor, getLocationEmoji } from '../../helpers/locations';
import { formatPatientName } from '../../helpers/patients';

/**
 * Renders a single appointment block overlayed on the week grid.
 * Expects appointment: { start, end, backgroundColor, title? }
 * and style: { top, left, width, height } in pixels.
 */
const CalendarBlock = ({
  timezone,
  session,
  style,
  isAwayTime = false,
  isAvailabilityBlock = false,
  onClick = () => console.log('Clicked session', session),
}) => {
  if (!session || !style) return null;
  console.log('Rendering appointment', session);

  const startDate = (
    typeof session.date === 'string'
    ? new Date(session.date)
    : session.date
  );
  const timeLabel = moment(startDate).tz(timezone || moment.tz.guess()).format('h:mm A');
  const locationCategory = (
    session?.appointment_scheduled?.session_type?.location_type
    || session?.session_type?.location_type
  );
  console.log('session', session);
  const backgroundColor = (
    isAvailabilityBlock
    ? tinycolor(session.css_color || '#4c98af').setAlpha(0.4).toRgbString()
    : isAwayTime
    ? 'rgba(200, 200, 200, 0.8)'
    : getLocationColor(locationCategory)
  );
  const opacity = moment(session.date).isBefore(moment()) ? 0.60 : 1;
  const border = (
    isAvailabilityBlock
    ? `1px solid ${session.css_color || '#4c98af'}`
    : undefined
  );
  const fontColor = isAvailabilityBlock ? (session.css_color || '#4c98af') : 'white';
  let zIndex = 4;
  if (isAwayTime) zIndex = 3;
  if (isAvailabilityBlock) zIndex = 2;
  return (
    <Box
      style={{
        cursor: isAwayTime ? 'default' : 'pointer',
        position: 'absolute',
        top: style.top,
        left: style.left,
        width: style.width,
        height: style.height,
        backgroundColor,
        color: fontColor,
        fontWeight: 700,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        zIndex,
        opacity,
        border,
      }}
      sx={{
        borderRadius: 2,
        pt: 0.5,
        pr: 1,
        pl: 1,
        pb: 0.5,
        boxShadow: 1,
        '&:hover': {
          opacity: 0.9,
          boxShadow: 2,
        },
      }}
      onClick={isAwayTime ? undefined : e => onClick(e, session.id)}
    >
      <Stack direction='row' justifyContent='space-between'>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: fontColor, lineHeight: 0.9 }}
        >
          {timeLabel}
        </Typography>
        <Typography>{isAwayTime ? '❌' : isAvailabilityBlock ? '⚙️' : getLocationEmoji(locationCategory)}</Typography>
      </Stack>
      {(!!session.patient || isAwayTime || isAvailabilityBlock) &&
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: fontColor,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 0.9,
            m: 0,
            p: 0,
          }}
        >
          {isAwayTime ? 'Away Time' : isAvailabilityBlock ? session?.title : formatPatientName(session.patient)}
        </Typography>
      }
      {!!session.duration && session.duration > 30 &&
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: 'white',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 0.9,
            pt: 0.5,
          }}
        >
          {(session.location !== 'VIRTUAL' ? session.location : '') || ''}
        </Typography>
      }
    </Box>
  );
};

export default CalendarBlock;
